<template>
  <div class="workflow-submit-form">
    <WorkflowEmailModal
      v-if="showEmailModal"
      :workflowType="wfSubmissionType"
      :workflowId="workflowId"
      :jobId="jobId"
      :wfSubmission="wfSubmission"
      :isExternal="true"
      :companyId="companyId"
      :companyName="companyName"
      :attachments="attachments"
      @closeModal="showEmailModal = false"
      open
    />
    <PersonalNotesModal
      v-if="showPersonalNotesModal"
      :workflowId="props.workflowId"
      :value="privateComment"
      @closeModal="closePersonalNotesModal"
    />

    <div class="workflow-submit-form__header-container">
      <span>Update and Submit</span>
      <Button
        v-if="showApproveButton"
        class="approve-button"
        theme-color="warning"
        type="button"
        @click="onApproveClicked"
        data-cy="approve-button"
        >Reviewed</Button
      >
    </div>

    <div
      v-if="privateComment != null && privateComment !== ''"
      class="comment-container"
    >
      <i>{{ privateComment }}</i>
    </div>

    <div class="workflow-submit-form__inputs-wrapper">
      <div class="workflow-submit-form__inputs">
        <div class="combobox-wrapper">
          <div>
            <div>Status</div>
            <ComboBox
              :data-items="validStatus()"
              :required="true"
              v-model="selectedStatus"
            />
          </div>

          <div>
            <div>Assign Work Item and Forward</div>
            <AutoComplete
              :data-items="groupedPersons"
              textField="name"
              groupField="func"
              :placeholder="'Start typing to search...'"
              :suggest="true"
              :value="selectedPersonName"
              @change="onPersonFilter"
              :required="true"
            />
          </div>

          <div class="workflow-submit-form__actions">
            <img
              class="private-notes-icon"
              :src="privateComment ? privateNotesIconActive : privateNotesIcon"
              alt="Update Private Note"
              data-cy="private-notes-action"
            />

            <Button
              type="button"
              @click="addPrivateNote"
              data-cy="add-private-note"
              class="email-link"
              >{{ privateComment ? "Modify" : "Add" }} Private Note</Button
            >
          </div>
          <div class="workflow-submit-form__actions">
            <FontAwesomeIcon
              v-if="sendEmailVisible"
              class="option-icon"
              icon="fa fa-envelope"
            />
            <Button
              v-if="sendEmailVisible"
              type="button"
              @click="showEmailModal = true"
              data-cy="send-email-button"
              class="email-link"
              >Send Email</Button
            >
          </div>
        </div>
      </div>

      <div class="workflow-submit-form__inputs upload-column">
        <div class="padding-between">
          <header class="form-header">
            <span data-cy="documentation-header-title">Add Documents</span>
          </header>

          <FileUpload v-model="uploadedDocs" />
        </div>

        <div>
          <span>Add Comments</span>
          <textarea
            v-model="enteredComments"
            rows="5"
            maxlength="500"
            data-cy="comments-input"
          ></textarea>
        </div>
      </div>
    </div>
    <LoadingButton
      :theme-color="'primary'"
      type="button"
      class="workflow-submit-form__submit-button"
      :loading="saving"
      @click="saveWorkflowDetailsAsync"
      data-cy="save-changes"
      >Save Changes</LoadingButton
    >
  </div>
</template>

<script setup lang="ts">
import {
  ComboBox,
  AutoComplete,
  type AutoCompleteChangeEvent,
} from "@progress/kendo-vue-dropdowns";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import {
  getAttachmentFilesAsync,
  validateTotalUploadSize,
} from "@/shared/helpers/fileHelper";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { api } from "@/api";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import PersonalNotesModal from "@/views/workflowOverview/PersonalNotesModal.vue";
import { UserRoles } from "@/Domain/UserRoles";
import { groupBy } from "@progress/kendo-data-query";
import { EnumJobFunction } from "@/Domain/EnumJobFunction";
import WorkflowEmailModal from "@/shared/ui/modal/WorkflowEmailModal.vue";
import { EnumWorkflowType } from "@/Domain/EnumWorkflowType";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import type { WfSubmissionDisplay } from "@/Domain/WfSubmissionDisplay";
import { Button } from "@progress/kendo-vue-buttons";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import privateNotesIconActive from "@/assets/img/pencil.png";
import privateNotesIcon from "@/assets/img/pencil_na.png";
import FileUpload from "@/shared/ui/inputs/FileUpload.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

interface AssignmentPerson {
  func: string;
  name: string;
  personJobId: number;
}

const props = defineProps<{
  jobId: string;
  workflowId: number;
  status: EnumWorkflowStatus;
  privateComment?: string;
  assignedTo: string;
  wfSubmission: WfSubmissionDisplay;
  wfSubmissionType: EnumWorkflowType;
  companyId: number;
  companyName: string;
  attachments: SharePointAttachment[];
  showApproveButton?: boolean;
  lineItemsStatus: string;
}>();

const emit = defineEmits<{
  (e: "formSubmitted"): void;
  (e: "approveClicked"): void;
}>();

const enteredComments = ref<string>("");
const uploadedDocs = ref<UploadFileInfo[]>([]);

const assignmentPersons = ref<AssignmentPerson[]>([]);
const selectedPersonName = ref<string>("");

const showEmailModal = ref<boolean>(false);
const showPersonalNotesModal = ref<boolean>(false);
const privateComment = ref<string>(props.privateComment ?? "");

const saving = ref<boolean>(false);

const groupedPersons = computed(() => {
  const groupedBy = (
    groupBy(assignmentPersons.value, [{ field: "func" }]) as any[]
  )
    .reduce((res: any[], group: any) => [...res, ...group.items], [])
    .sort((a, b) => {
      // Sort by function (group)
      if (a.func < b.func) return -1;
      if (a.func > b.func) return 1;

      // Sort by name
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;

      // If both function and name are the same, return 0
      return 0;
    });

  if (selectedPersonName.value) {
    const filtered = groupedBy.filter((person) =>
      person.name.toLowerCase().includes(selectedPersonName.value.toLowerCase())
    );
    return filtered;
  }

  return groupedBy;
});

const sendEmailVisible = computed(() => {
  const isIngredientRole = main.isInRole(UserRoles.ROLE_INGREDIENT);
  return (
    props.wfSubmissionType == EnumWorkflowType.Product ||
    !isIngredientRole ||
    (isIngredientRole &&
      (main.isInRole(UserRoles.ROLE_RABBINIC_COORDINATOR) ||
        main.isInRole(UserRoles.ROLE_RC_SUPPORT_STAFF) ||
        main.isInRole(UserRoles.ROLE_MIS)))
  );
});

const saveWorkflowDetailsAsync = async function () {
  if (saving.value) return;

  try {
    saving.value = true;

    // Validate total files size
    if (!validateTotalUploadSize(uploadedDocs.value)) {
      saving.value = false;
      return;
    }

    const newStatus =
      EnumWorkflowStatus[
        selectedStatus.value as keyof typeof EnumWorkflowStatus
      ];

    const personJobId =
      assignmentPersons.value.find(
        (person) => person.name?.toLowerCase() === selectedPersonName.value
      )?.personJobId ?? -1;
    const files = await getAttachmentFilesAsync(uploadedDocs.value);

    await handleResponsePromiseAsync(
      api.post("SaveWorkflowDetails", {
        userLogin: main.userLogin,
        companyLogin: main.companyLogin,
        JobId: props.jobId,
        WorkflowId: props.workflowId,
        PersonJobId: personJobId > -1 ? personJobId.toString() : "",
        Comments: enteredComments.value,
        Status: newStatus,
        WorkflowType: props.wfSubmissionType,
        Files: files,
      }),
      () => {
        notificationStore.notify({
          message: "Workflow details saved successfully",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
        enteredComments.value = "";
        uploadedDocs.value = [];
        selectedPersonName.value = "";
        emit("formSubmitted");
      },
      "WorkflowSubmitForm.vue",
      "saveWorkflowDetailsAsync",
      () => {
        notificationStore.notify({
          message: "Error saving workflow details. Please try again",
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
      }
    );
  } finally {
    saving.value = false;
  }
};

const getAssignmentPersonAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetAssignmentPersonJobTbs", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId:
        props.wfSubmissionType == EnumWorkflowType.Product
          ? props.workflowId
          : Number(props.jobId) ?? 0,
      workflowType: EnumWorkflowType[props.wfSubmissionType],
    }),
    (data) => {
      assignmentPersons.value = data
        .filter((person) => person.Function && person.FormattedName)
        .map((person) => {
          return {
            func: person.Function
              ? EnumJobFunction[person.Function].toString()
              : "",
            name: person.FormattedName,
            personJobId: person.PersonJobId,
          };
        });
    },
    "WorkflowSubmitForm.vue",
    "getAssignmentPersonAsync"
  );
};

const addPrivateNote = function () {
  showPersonalNotesModal.value = true;
};

const closePersonalNotesModal = function (updated: boolean, newNotes: string) {
  if (updated) privateComment.value = newNotes;
  showPersonalNotesModal.value = false;
};

const closeRole = [
  UserRoles.ROLE_GROUP_LEADER,
  UserRoles.ROLE_RABBINIC_COORDINATOR,
  UserRoles.ROLE_RC_SUPPORT_STAFF,
  UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
];

const managerRole = [
  UserRoles.ROLE_IN_HOUSE_MIS_MANAGER,
  UserRoles.ROLE_PRODUCT_SUPERVISOR,
];

const validStatus = function () {
  const status = [] as string[];

  if (props.status === EnumWorkflowStatus.Open) {
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.InProgress]);
    if (
      main.isInAnyRole(closeRole) &&
      props.lineItemsStatus === EnumWorkflowStatus[EnumWorkflowStatus.Closed]
    )
      status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);
  }

  if (props.status === EnumWorkflowStatus.Closed) {
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.Reopened]);
  }

  if (props.status === EnumWorkflowStatus.Reopened) {
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.Reopened]);
    if (
      main.isInAnyRole(closeRole) &&
      props.lineItemsStatus === EnumWorkflowStatus[EnumWorkflowStatus.Closed]
    )
      status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);
  }

  if (props.status === EnumWorkflowStatus.AutoProcessed) {
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.InProgress]);
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);
  }

  if (
    props.status === EnumWorkflowStatus.InProgress ||
    props.status === EnumWorkflowStatus.RcReviewed
  ) {
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.InProgress]);
    if (
      main.isInAnyRole(closeRole) &&
      props.lineItemsStatus === EnumWorkflowStatus[EnumWorkflowStatus.Closed]
    )
      status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);
  }

  if (
    props.status !== EnumWorkflowStatus.Closed &&
    main.isInAnyRole(managerRole) &&
    !status.includes(EnumWorkflowStatus[EnumWorkflowStatus.Closed])
  )
    status.push(EnumWorkflowStatus[EnumWorkflowStatus.Closed]);

  return status;
};

const selectedStatus = ref<string>(validStatus()[0]);

const onPersonFilter = function (e: AutoCompleteChangeEvent) {
  selectedPersonName.value = e.target.value?.toLowerCase();
};

const onApproveClicked = function () {
  emit("approveClicked");
};

defineExpose({
  saveWorkflowDetailsAsync,
});

watchEffect(() => {
  privateComment.value = props.privateComment ?? "";
});

onBeforeMount(async () => {
  await getAssignmentPersonAsync();
});
</script>

<style scoped lang="scss">
.workflow-submit-form {
  background-color: #ffffff;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-basis: 320px;
  font-size: 13px;
  text-wrap: wrap;

  &__header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    font-size: 14px;
    background: #dedede;
    border-bottom: 1px solid #e8e8e8;
    font-weight: bold;
  }

  .comment-container {
    display: flex;
    padding: 5px; // 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 0 35px rgb(0 0 0 / 10%);
    background-color: #f6f4bf;
    margin: 8px 12px;
  }

  &__inputs-wrapper {
    display: flex;
    padding: 8px;
    gap: 12px;
    flex-wrap: wrap;
    flex-direction: row-reverse;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    min-width: 220px;

    & textarea {
      resize: none;
      font-weight: normal;
    }

    &.upload-column {
      flex: 1 1 30%;
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    font-weight: normal;
    align-items: center;
  }

  .combobox-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .k-input {
      max-width: 260px;
    }
  }

  &__submit-button {
    margin: 8px auto;
    width: 150px;
  }
}

.cc-label-name {
  white-space: pre;
  color: #999;
  font-weight: 700;
  font-size: smaller;
  display: flex;
  text-wrap: wrap;
  .smaller {
    font-size: 12px;
  }
}

.upload-container .comments-input {
  max-width: 150px;
}

.k-input-inner {
  font-size: 12px;
  width: 80%;
}

.upper-padding {
  padding-top: 10px;
}

.upper-padding {
  padding-top: 30px;
}

.padding-between {
  padding-right: 10px;
}

.email-link {
  background-color: transparent;
  color: #337ab7;
  border: none;
  display: inline-block;

  &:focus {
    box-shadow: none;
  }
}

.private-notes-icon {
  cursor: pointer;
  max-height: 50%;
}
</style>
